import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { OrderAndHistorySustainabilityMetrics, SustainabilityMetrics } from '@/models/sustainability';
import { ui } from '@/constants';
import { 
  AnalyticsEventCategory,
  AnalyticsEventAction,
  AnalyticsEventLabel
} from '@/services/google/google-analytics.services'
import { AccountPageLoadEvent } from '@/services/analytics/events';

const currentYear = new Date().getFullYear();

@Component({
  name: 'CmgSustainabilityInfo',
})
export default class CmgSustainabilityInfo extends Vue {
  @Prop({ required: false, type: Object })
  orderAndHistorySustainabilityMetrics?: OrderAndHistorySustainabilityMetrics;

  showYourOrderMetrics = true;
  didError = false;
  isLoading = false;

  mounted() {
    this.didError = this.$appStore.state.checkout.sustainability.didError;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth < ui.breakpoints.tablet;
  }

  get isCustomerLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  get foodprintDoodles(): string {
    return this.isMobile ? this.$appStore.state.cms.images.backgrounds.foodprintDoodles.default : this.$appStore.state.cms.images.backgrounds.foodprintDoodlesDestkop.default;
  }

  get currentYear(): number {
    return currentYear;
  }

  get sustainabilityMetrics(): SustainabilityMetrics | null {
    if (!this.orderAndHistorySustainabilityMetrics) {
      return null;
    }

    return this.showYourOrderMetrics ? this.orderAndHistorySustainabilityMetrics.order : this.orderAndHistorySustainabilityMetrics.history;
  }

  get realFoodprintUrl(): string {
    return this.$services.config.getRealFoodprintUrl();
  }

  get hasNoSusHistory(): boolean {
    return !(this.orderAndHistorySustainabilityMetrics && this.orderAndHistorySustainabilityMetrics.history && this.orderAndHistorySustainabilityMetrics.history.numOfOrders);
  }

  onClickLearnMore(): void {
    window.open(this.realFoodprintUrl, '_blank');
  }

  onClickSignIn(): void {
    this.$services.ui.setModalVisibility('signIn', true);
    this.$services.analytics.send(new AccountPageLoadEvent('sign-in'));
  }

  onClickThisOrderToggle(): void {
    this.showYourOrderMetrics = true;
    this.$services.googleAnalytics.sendCustomEvent({ 
      category: AnalyticsEventCategory.Confirmation,
      action: AnalyticsEventAction.OrderImpactClick,
      label: AnalyticsEventAction.OrderImpactClick,
    });
  }

  onClickFullImpactToggle(): void {
    this.showYourOrderMetrics = false;
    this.$services.googleAnalytics.sendCustomEvent({ 
      category: AnalyticsEventCategory.Confirmation,
      action: AnalyticsEventAction.CumulativeImpactClick,
      label: AnalyticsEventAction.CumulativeImpactClick,
    });
  }

  async fetchOrderHistory(): Promise<void> {
    const postSustainabilityMetricsBody = this.$appStore.state.checkout.sustainability.postSustainabilityMetricsBody;
    if (!postSustainabilityMetricsBody) {
      this.$services.logger.unreachable('Sustainability POST failed on checkout page, but postSustainabilityMetricsBody was undefined in store');
      return this.$services.ui.setModalVisibility('globalErrorToast', true);
    }

    this.isLoading = true;
    try {
      await this.$services.sustainability.postCurrentOrderAndHistorySustainabilityMetrics(postSustainabilityMetricsBody);
      this.didError = false;
      // Reset state in store in case user navigates away from order conf page, then comes back
      this.$services.checkout.setSustainabilityError({ didError: false });
    } catch (e: any) {
      this.$services.logger.error('Error POSTing sustainability metrics', postSustainabilityMetricsBody, e);
    } finally {
      this.isLoading = false;
    }
  }
}
